import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';

import { useFormatMessage } from 'hooks';
import Table from 'components/Table';
import { fetchAuthorities, deleteAuthority } from 'state/actions/authorities';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import classes from './Authorities.module.scss';

const Authorities = () => {
  const { authorities, error, loading, deleted } = useSelector(
    (state) => ({
      authorities: state.authorities.data,
      error: state.authorities.error,
      loading: state.authorities.loading,
      deleted: state.authorities.deleted,
    }),
    shallowEqual
  );

  const [deleteModal, setDeleteModal] = useState({
    authorityId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchAuthorities());
  }, [dispatch]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal({
        authorityId: null,
        isOpen: false,
      });
    }
  }, [deleted, loading]);

  const onRemoveButtonClickHandler = (authorityId) => {
    setDeleteModal((prevState) => ({
      authorityId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ authorityId: null, isOpen: false });
  };

  const onDeleteAuthorityHandler = () => {
    dispatch(deleteAuthority(deleteModal.authorityId));
  };

  const columns = [
    {
      Header: useFormatMessage('Authorities.id'),
      accessor: 'index',
      Cell: ({ row }) => row.original.id,
    },
    {
      Header: useFormatMessage('Authorities.name'),
      accessor: 'name',
      Cell: ({ row }) => (
        <Link to={`/authorities/${row.original.id}`}>{row.original.name}</Link>
      ),
    },
    {
      Header: useFormatMessage('Authorities.address'),
      id: 'address',
      accessor: 'address',
    },
    {
      Header: useFormatMessage('Authorities.posts'),
      id: 'posts',
      accessor: 'posts',
      Cell: ({ row }) => (
        <div className="buttons is-center">
          <Link
            to={`/authorityPosts/${row.original.id}`}
            className="button is-small is-primary"
          >
            <span className="icon is-small">
              <i className="mdi mdi-square-edit-outline" />
            </span>
          </Link>
        </div>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="buttons is-right">
          <Link
            to={`/authorities/${row.original.id}`}
            className="button is-small is-primary"
          >
            <span className="icon is-small">
              <i className="mdi mdi-square-edit-outline" />
            </span>
          </Link>

          <button
            type="button"
            className="button is-small is-danger"
            onClick={() => onRemoveButtonClickHandler(row.original.id)}
          >
            <span className="icon is-small">
              <i className="mdi mdi-trash-can" />
            </span>
          </button>
        </div>
      ),
      disableSortBy: true,
    },
  ];

  const data = search
    ? authorities.filter((el) => {
        const clonedElem = { ...el };
        delete clonedElem.createdAt;
        delete clonedElem.updatedAt;
        delete clonedElem.createdBy;
        delete clonedElem.updatedBy;
        return Object.values(clonedElem).some((field) => {
          const key = field instanceof Date ? field.toDateString() : `${field}`;
          return key.toLowerCase().includes(search.toLowerCase());
        });
      })
    : authorities;

  const deleteMessage = useFormatMessage('Categories.delete');

  const confirmMessage = useFormatMessage('Categories.confirm');

  const permDeleteMessage = useFormatMessage('Authorities.permDelete');

  const cancelMessage = useFormatMessage('Categories.cancel');

  return (
    <>
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteAuthorityHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">
                  {useFormatMessage('Authorities.title')}
                </h1>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <Link to={paths.ADD_AUTHORITY} className="button">
                  {useFormatMessage('Authorities.new')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        <div className="card has-table has-mobile-sort-spaced">
          <header className="card-header">
            <p className={classNames('card-header-title', classes.tableHeader)}>
              <span>{useFormatMessage('Categories.search')}</span>
              <input
                type="text"
                className="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </p>
          </header>
          <div className="b-table">
            {loading ? <ClipLoader /> : <Table columns={columns} data={data} />}
            {error && 'Show error'}
          </div>
        </div>
      </section>
    </>
  );
};

export default Authorities;
